import { useDebounce } from 'react-use';
import { useCallback, useState } from 'react';

import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { TextEditorElementRef } from '@dreryk/react-components';
import { useAverageKeyPressSpeed } from '@dreryk/react-common';
import { TextEditorValue } from '@dreryk/react-text-editor';

import { useTextEditorFieldDictation, UseTextEditorFieldDictationResult } from '~libs.desktop.ai-assistant';

import { VisitField } from '../../../common/visitForm/VisitFormContext.types';
import { useVisitFormContext } from '../../../common/visitForm/VisitFormContext';

import { OnVisitFormTextEditorAutoSave } from './VisitFormTextEditor.types';

const AUTOSAVE_DELAY_THRESHOLD = 500;
const DEFAULT_AVERAGE_KEY_PRESS_SPEED = 300;

type AutoSaveDelay = number;
type UseAutosaveDelayOptions = {
   practitionerId: PractitionerId;
   element: HTMLElement | null;
};

const useTextEditorAutoSaveDelay = (options: UseAutosaveDelayOptions): AutoSaveDelay => {
   const [averageKeyPressTime] = useAverageKeyPressSpeed(
      options.practitionerId.asNumber(),
      [options.element],
      DEFAULT_AVERAGE_KEY_PRESS_SPEED
   );

   return averageKeyPressTime ? averageKeyPressTime + AUTOSAVE_DELAY_THRESHOLD : DEFAULT_AVERAGE_KEY_PRESS_SPEED;
};

type UseTextEditorAutosaveOptions = {
   onAutoSave: OnVisitFormTextEditorAutoSave;
   practitionerId: PractitionerId;
   isDirty: boolean;
   value: TextEditorValue | null;
   disabled?: boolean;
};

type UseTextEditorAutosaveReturnFn = {
   setTextEditorElement: TextEditorElementRef;
};

export const useTextEditorAutosave = (options: UseTextEditorAutosaveOptions): UseTextEditorAutosaveReturnFn => {
   const [textEditorElement, setTextEditorElement] = useState<HTMLElement | null>(null);
   const delay = useTextEditorAutoSaveDelay({
      element: textEditorElement,
      practitionerId: options.practitionerId,
   });

   useDebounce(
      () => {
         if (options.isDirty && !options.disabled) {
            options.onAutoSave({
               value: options.value,
            });
         }
      },
      delay,
      [options.value?.getFormattedValue()]
   );

   return {
      setTextEditorElement: setTextEditorElement,
   };
};

type UseVisitFormDictationOptions = {
   field: VisitField;
};

export const useVisitFormDictation = (options: UseVisitFormDictationOptions): UseTextEditorFieldDictationResult => {
   const { getFieldHtml, setFieldHtml } = useVisitFormContext();

   const getHtml = useCallback(() => getFieldHtml(options.field), [getFieldHtml, options.field]);

   const setHtml = useCallback((html: string) => setFieldHtml(options.field, html), [setFieldHtml, options.field]);

   return useTextEditorFieldDictation({ getHtml: getHtml, setHtml: setHtml });
};
